import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { Spin } from "antd";

import css from "./CloneFromDateMenuComponent.module.css";
import { cycleDayMenuActions } from "../../../store/slices/menu-slices";
import { menuMenuService } from "../../../services/menu-services/menuMenu.service";
import { cycleMenuService } from "../../../services/menu-services/cycleMenu.service";
import { ImageReusable } from "../../../ui";

const CloneFromDateMenuComponent = ({ activeDay }) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    const [isFetching, setIsFetching] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewPacks, setPreviewPacks] = useState([]);
    const [pendingDate, setPendingDate] = useState(null);

    // Step 1: fetch and show preview
    const submit = async (data) => {
        if (!data?.date) return;
        setIsFetching(true);
        try {
            const menuPacks = await menuMenuService.getAllMenuPacks(data.date, true);

            if (!menuPacks || menuPacks.length === 0) {
                toast.warning(t("No menu found for the selected date"));
                return;
            }

            setPendingDate(data.date);
            setPreviewPacks(menuPacks);
            setPreviewOpen(true);
        } catch (error) {
            console.error("Error fetching menu for preview:", error);
            toast.error(t("Error fetching menu"));
        } finally {
            setIsFetching(false);
        }
    };

    // Step 2: execute clone after user confirms in modal
    const handleConfirmClone = async () => {
        setIsCloning(true);
        try {
            const createPromises = [];
            for (const pack of previewPacks) {
                for (const menuDish of pack.menuDishes || []) {
                    createPromises.push(
                        cycleMenuService.create({
                            cycleDay: activeDay,
                            packId: pack.id,
                            mealId: menuDish.mealId,
                            dishId: menuDish.dish.id,
                            ...(menuDish.type && { type: menuDish.type }),
                            ...(menuDish.proteinDishId && { proteinDishId: menuDish.proteinDishId }),
                            ...(menuDish.sauceDishId && { sauceDishId: menuDish.sauceDishId }),
                        })
                    );
                }
            }
            await Promise.all(createPromises);

            dispatch(cycleDayMenuActions.getAllCycleDayMenu(activeDay));
            toast.success(t("Menu cloned from date base menu successfully"));
            setPreviewOpen(false);
        } catch (error) {
            console.error("Error cloning menu:", error);
            toast.error(t("Error cloning menu"));
        } finally {
            setIsCloning(false);
        }
    };

    const closeModal = () => {
        if (!isCloning) setPreviewOpen(false);
    };

    const totalDishCount = previewPacks.reduce((sum, p) => sum + (p.menuDishes?.length || 0), 0);

    // Group packs by their packCategory
    const categoryGroups = (() => {
        const map = new Map();
        for (const pack of previewPacks) {
            const cat = pack.packCategory;
            const key = cat?.id ?? '__none__';
            if (!map.has(key)) map.set(key, { category: cat ?? null, packs: [] });
            map.get(key).packs.push(pack);
        }
        const groups = Array.from(map.values());
        groups.sort((a, b) => {
            if (!a.category && b.category) return 1;
            if (a.category && !b.category) return -1;
            return 0;
        });
        return groups;
    })();

    return (
        <>
            <form onSubmit={handleSubmit(submit)} className={css.form}>
                <label className={css.label}>{t("Clone from Date Base Menu")}</label>
                <input
                    type="date"
                    {...register("date", { required: "Date is required" })}
                    className={`${css.input} ${errors.date ? css.inputError : ""}`}
                />
                {errors.date && <p className={css.error}>{errors.date.message}</p>}
                <button type="submit" disabled={isSubmitting || isFetching} className={css.button}>
                    {isFetching ? t("Loading...") : "Ok"}
                </button>
            </form>

            {previewOpen && (
                <div className={css.overlay} onClick={closeModal}>
                    <div className={css.modal} onClick={(e) => e.stopPropagation()}>

                        <div className={css.modalHeader}>
                            <h2>
                                {t("Clone from")} <strong>{pendingDate}</strong> → {t("Day")} <strong>{activeDay}</strong>
                            </h2>
                            <button className={css.closeButton} onClick={closeModal} disabled={isCloning}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={css.modalBody}>
                            <p className={css.sectionTitle}>
                                {t("Menu dishes to clone")} <span className={css.badge}>{totalDishCount}</span>
                            </p>

                            {categoryGroups.map(({ category, packs }) => (
                                <div key={category?.id ?? '__none__'} className={css.categoryBlock}>
                                    <div className={css.categoryHeader}>
                                        {category?.icon && (
                                            <img src={category.icon} alt="" className={css.categoryIcon} />
                                        )}
                                        <span className={css.categoryName}>
                                            {category
                                                ? (category.name?.[currentLanguage] || category.name?.en || "")
                                                : t("Uncategorized")}
                                        </span>
                                        <div className={css.categoryDivider} />
                                    </div>

                                    {packs.map((pack) => {
                                        const packName = pack.name?.[currentLanguage] || pack.name?.en || pack.name?.ru || `Pack ${pack.id}`;
                                        return (
                                            <div key={pack.id} className={css.packBlock}>
                                                <div className={css.packName}>{packName}</div>
                                                <div className={css.grid}>
                                                    {(pack.menuDishes || []).map((md, idx) => {
                                                        const dish = md.dish;
                                                        const name = dish?.name?.[currentLanguage] || dish?.name?.en || "";
                                                        const img = dish?.img;
                                                        const weight = dish?.weight;
                                                        return (
                                                            <div key={idx} className={css.card}>
                                                                <ImageReusable src={img} alt={name} className={css.cardImage} />
                                                                <div className={css.cardBody}>
                                                                    <div className={css.cardName}>{name}</div>
                                                                    {weight != null && (
                                                                        <span className={css.cardMeta}>{weight}g</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>

                        <div className={css.modalFooter}>
                            <button className={css.cancelButton} onClick={closeModal} disabled={isCloning}>
                                {t("Cancel")}
                            </button>
                            <button className={css.confirmButton} onClick={handleConfirmClone} disabled={isCloning}>
                                {isCloning ? <Spin size="small" /> : t("Confirm Clone")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { CloneFromDateMenuComponent };
